/**
 * File Download Bar for Session Viewer
 *
 * Shows a notification bar when a file is available for download.
 * Appears at the bottom of the viewer page. Touch-friendly on mobile.
 */

import { Download, File } from 'lucide-react'
import { formatBytes, type SessionFileInfo } from '@polygraf/shared'

interface FileDownloadBarProps {
	file: SessionFileInfo
	downloadUrl: string
}

function getFileIcon(mimeType: string): string {
	if (mimeType.includes('pdf')) return 'PDF'
	if (mimeType.includes('presentation') || mimeType.includes('pptx')) return 'PPTX'
	if (mimeType.includes('document') || mimeType.includes('docx') || mimeType.includes('word')) return 'DOC'
	if (mimeType.includes('spreadsheet') || mimeType.includes('xlsx') || mimeType.includes('excel')) return 'XLS'
	if (mimeType.includes('image')) return 'IMG'
	if (mimeType.includes('video')) return 'VID'
	if (mimeType.includes('audio')) return 'AUD'
	if (mimeType.includes('zip') || mimeType.includes('archive') || mimeType.includes('tar')) return 'ZIP'
	return 'FILE'
}

export function FileDownloadBar({ file, downloadUrl }: FileDownloadBarProps) {
	const fileTypeLabel = getFileIcon(file.mimeType)

	return (
		<div className="animate-enter bg-black/90 border-t border-white/10 px-3 sm:px-4 py-2.5 flex items-center gap-3 shrink-0">
			<File size={18} className="text-blue-400 shrink-0" />
			<span className="text-xs font-mono bg-white/10 text-white/70 px-2 py-1 rounded shrink-0">
				{fileTypeLabel}
			</span>
			<span className="text-sm text-white/80 truncate flex-1 min-w-0">{file.originalName}</span>
			<span className="text-xs text-white/40 shrink-0 hidden sm:inline">
				{formatBytes(file.sizeBytes)}
			</span>
			<a
				href={downloadUrl}
				download
				className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-500 active:bg-blue-700 transition-colors"
			>
				<Download size={16} />
				<span className="hidden sm:inline">Download</span>
			</a>
		</div>
	)
}
